import { Flex, Table, TableProps, Typography } from "antd"
import { DqiValue } from "src/pages/DataHealthDashboard/constants"
import { graphql } from "src/api/graphql/gql.ts"
import { DatasourceTypeV2 } from "src/api/graphql/graphql"
import { formatLocalCalendarDateWithTime } from "src/global/utils/formatTime"
import { SkeletonTable, SkeletonTableColumnsType } from "src/global/components/SkeletonTable"
import { exists } from "src/global/utils/typeguards"
import { styled } from "styled-components"
import { getImageLogo } from "src/pages/DataAssets/getImageLogo"
import { theme } from "src/global/ui/themes/theme"
import { Icon } from "src/global/ui/Icon/Icon"
import { useUrlParams } from "src/global/utils/urlParams"
import { FilterDateRangeDropdown } from "src/pages/DataAssets/components/FilterDateRangeDropdown"
import {
  convertFilterObjectToFilterParams,
  convertSortParamsToSortValue,
  convertSortValueToSortParams,
  filterByStringField,
  filterByDateRange,
  sortByStringField,
  sortByDateField,
  processFilterValueParam,
} from "src/pages/DataAssets/components/dataAssetsTableUtils"
import { useMemo } from "react"
import { uniq } from "lodash-es"
import { useOrganizationSlug } from "src/pages/OrganizationRoot/hooks/useOrganizationSlug"
import { unmaskFragment } from "src/api/graphql/fragment-masking"
import { useOutletContext } from "react-router-dom"
import type { DataHealthDashboardOutletContext } from "src/pages/DataHealthDashboard/DataHealthDashboard"

export const InactiveAssetsTableFragment = graphql(`
  fragment InactiveAssetsTable on DatasourceV2 {
    id
    type
    name
    assets {
      id
      aggregateSuccessStatus
      lastValidatedAt
      name
      coverageStats {
        isActive
        hasCompletenessCoverage
        hasSchemaCoverage
        hasVolumeCoverage
        hasUniquenessCoverage
        hasNumericCoverage
        hasValidityCoverage
      }
      checkpoints {
        validations {
          expectationSuiteV2 {
            id
          }
        }
      }
    }
  }
`)

type InactiveAssetsTableData = {
  id: string
  name: string
  type: DatasourceTypeV2
  dataSourceName: string
  lastValidatedAt: string | null
  aggregateSuccessStatus: boolean
  defaultExpectationSuiteId: string | null
  coverageStats: {
    isActive: boolean
    hasCompletenessCoverage: boolean
    hasSchemaCoverage: boolean
    hasVolumeCoverage: boolean
    hasUniquenessCoverage: boolean
    hasNumericCoverage: boolean
    hasValidityCoverage: boolean
  }
}

type UrlParams = {
  filter: string
  sort: string
}

const getTableTitle = (dqi: DqiValue): string => {
  if (dqi !== "overall") {
    return `Data Assets inactive for ${dqi}`
  }
  return "Inactive Data Assets"
}

export const InactiveAssetsTable = () => {
  const { datasources, datasourcesLoading, dqi } = useOutletContext<DataHealthDashboardOutletContext>()
  const [params, setUrlParams] = useUrlParams<UrlParams>({ filter: "", sort: "" })

  const { filter, sort: sortParam } = params
  const {
    dataAssets: dataAssetsParam = undefined,
    dataSources: dataSourcesParam = undefined,
    lastValidation: lastValidationParam = undefined,
  } = filter ? JSON.parse(filter) : {}
  const sort = convertSortParamsToSortValue(sortParam)
  const { navigateInOrg } = useOrganizationSlug()

  const datasourcesList = unmaskFragment(InactiveAssetsTableFragment, datasources) ?? []

  const allAssetData: InactiveAssetsTableData[] =
    datasourcesList.flatMap((dataSource) =>
      dataSource.assets.filter(exists).map((asset) => ({
        id: asset.id,
        name: asset.name,
        type: dataSource.type,
        dataSourceName: dataSource.name,
        lastValidatedAt: asset.lastValidatedAt ?? null,
        aggregateSuccessStatus: asset.aggregateSuccessStatus ?? false,
        defaultExpectationSuiteId: asset.checkpoints[0]?.validations[0]?.expectationSuiteV2?.id ?? null,
        coverageStats: asset.coverageStats ?? {
          isActive: false,
          hasCompletenessCoverage: false,
          hasSchemaCoverage: false,
          hasVolumeCoverage: false,
          hasUniquenessCoverage: false,
          hasNumericCoverage: false,
          hasValidityCoverage: false,
        },
      })),
    ) ?? []

  const assetData = allAssetData.filter((asset) => !asset.coverageStats.isActive || !hasDqiCoverage(asset, dqi))

  const dataAssetNames = useMemo(() => {
    return assetData?.map((asset) => asset.name) ?? []
  }, [assetData])

  const uniqueDataSources = useMemo(() => {
    return uniq(assetData?.map((asset) => asset.dataSourceName) ?? [])
  }, [assetData])

  const handleChange: TableProps["onChange"] = (_, filters, sorter) => {
    const sorterIsArray = Array.isArray(sorter)
    setUrlParams({
      filter: convertFilterObjectToFilterParams(filters),
      sort: convertSortValueToSortParams(
        sorterIsArray ? sorter[0]?.columnKey : sorter?.columnKey,
        sorterIsArray
          ? (sorter[0]?.order as "ascend" | "descend" | undefined)
          : (sorter?.order as "ascend" | "descend" | undefined),
      ),
    })
  }

  const columns: TableProps<InactiveAssetsTableData>["columns"] = [
    {
      title: "Data Asset",
      key: "name",
      dataIndex: "name",
      sorter: (a: InactiveAssetsTableData, b: InactiveAssetsTableData) => sortByStringField(a.name, b.name),
      sortOrder: sort.columnKey === "name" ? sort.order : undefined,
      filteredValue: dataAssetsParam ? dataAssetsParam.split(",") : undefined,
      filters: dataAssetNames.map((name) => ({ text: name, value: name })),
      onFilter: (value, record) => filterByStringField(value as string, record.name),
      width: 300,
      ellipsis: true,
      render: (name, { type }) => (
        <Flex gap={theme.spacing.xs} align="center" style={{ overflow: "hidden", height: "100%" }}>
          <img src={getImageLogo(type)} alt={`${type} logo`} width={22} height={22} />
          <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{name}</div>
        </Flex>
      ),
    },
    {
      title: "Data Source name",
      key: "dataSourceName",
      dataIndex: "dataSourceName",
      sorter: (a: InactiveAssetsTableData, b: InactiveAssetsTableData) =>
        sortByStringField(a.dataSourceName, b.dataSourceName),
      sortOrder: sort.columnKey === "dataSourceName" ? sort.order : undefined,
      filteredValue: dataSourcesParam ? dataSourcesParam.split(",") : undefined,
      filters: uniqueDataSources.map((source) => ({ text: source, value: source })),
      onFilter: (value, record) => record.dataSourceName === value,
      width: 250,
      ellipsis: true,
    },
    {
      title: "Last validated",
      key: "lastValidation",
      dataIndex: "lastValidatedAt",
      sorter: (a: InactiveAssetsTableData, b: InactiveAssetsTableData) =>
        sortByDateField(a.lastValidatedAt, b.lastValidatedAt),
      sortOrder: sort.columnKey === "lastValidation" ? sort.order : undefined,
      filterDropdown: FilterDateRangeDropdown,
      filteredValue: processFilterValueParam(lastValidationParam),
      onFilter: (value, record) => filterByDateRange(record.lastValidatedAt, value as string),
      width: 200,
      ellipsis: true,
      render: (timestamp: string | null) => (timestamp ? formatLocalCalendarDateWithTime(timestamp) : null),
    },
  ]

  const tableTitle = getTableTitle(dqi)

  return (
    <StyledTableWrapper>
      <SkeletonTable
        loading={datasourcesLoading}
        columns={columns as SkeletonTableColumnsType[]}
        title={() => <TableHeader title={tableTitle} />}
      >
        <Table<InactiveAssetsTableData>
          columns={columns}
          dataSource={assetData}
          rowKey="id"
          virtual
          pagination={false}
          title={() => <TableHeader title={tableTitle} />}
          onChange={handleChange}
          onRow={(asset) => ({
            onClick: () => navigateInOrg(dataAssetLink(asset)),
            style: { cursor: "pointer" },
          })}
        />
      </SkeletonTable>
    </StyledTableWrapper>
  )
}

const StyledTableWrapper = styled.div`
  border-radius: ${({ theme }) => theme.radius.default}px;
  border: 1px solid ${({ theme }) => theme.colors.neutralColorPalette.backgroundsAndBorders.gxBorder};
  overflow: hidden; /* This will ensure the table doesn't overflow the rounded corners */
`

function TableHeader({ title }: { title: string }) {
  return (
    <Flex align="center" gap={theme.spacing.xs}>
      <Icon name="warning" small color={theme.colors.warning.colorWarning} />
      <Typography.Text strong>{title}</Typography.Text>
    </Flex>
  )
}

const hasDqiCoverage = (asset: InactiveAssetsTableData, dqi: DqiValue): boolean => {
  switch (dqi) {
    case "overall":
      return true // not really filtering for overall
    case "schema":
      return asset.coverageStats.hasSchemaCoverage
    case "volume":
      return asset.coverageStats.hasVolumeCoverage
    case "completeness":
      return asset.coverageStats.hasCompletenessCoverage
    case "uniqueness":
      return asset.coverageStats.hasUniquenessCoverage
    case "numeric":
      return asset.coverageStats.hasNumericCoverage
    case "validity":
      return asset.coverageStats.hasValidityCoverage
  }
}

const dataAssetLink = (asset: InactiveAssetsTableData) => {
  if (asset.defaultExpectationSuiteId) {
    return `/data-assets/${asset.id}/validations/expectation-suites/${asset.defaultExpectationSuiteId}/results`
  }
  return `/data-assets/${asset.id}/expectations`
}
